//Libraries
import { NakedButton } from '@cb/apricot-react';
import { styled } from '@mui/system';

//Providers
import { useAuth } from 'src/contexts';

//Typing
import { TAIFormProcessStatus, ISummary } from 'src/types';

// Icons
import ArticleIcon from '@mui/icons-material/Article';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

const ActionList = styled('div')`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
`;

const ActionButton = styled(NakedButton)`
  padding: 0;
`;

interface RequestActionsProps {
  request: ISummary;
  type: 'ai' |'di';
  onStatusUpdate: (request: ISummary, processStatus: TAIFormProcessStatus) => void;
  onClickApprove: (request: ISummary) => void;
  onClickDenied: (request: ISummary) => void;
  onClickXpmDenied: (request: ISummary) => void;
  onClickNeedMoreInfo: (request: ISummary) => void;
  onClickOpsApproved: (request: ISummary) => void;
}

export const RequestActions = ({
  request,
  type,
  onClickApprove,
  onClickDenied,
  onClickNeedMoreInfo,
  onClickXpmDenied,
  onStatusUpdate,
  onClickOpsApproved
}: RequestActionsProps) => {
  const { roles } = useAuth();
  const isAdmin = roles.includes('XPM_ADMIN') || roles.includes('AI_ADMIN');
  const isQc = roles.includes('QUALITY_CONTROL') || isAdmin;
  const isReviewer = roles.includes('REVIEWER') && !isAdmin; //some users have multiple roles assigned to same username
  const handleClickApproved = () => {
    onClickApprove(request);
  };

  const handleClickPendingReview = () => {
    onStatusUpdate(request, 'processing');
  };

  const handleClickDenied = () => {
    onClickDenied(request);
  };

  const handleXpmDenied = () => {
    onClickXpmDenied(request);
  };

  const handleClickNeedMoreInformation = () => {
    onClickNeedMoreInfo(request);
  };

  const handleClickPendingAppeal = () => {
    onStatusUpdate(request, 'appeal');
  };

    const handleClickPendingDeactivationApproval = () => {
        onStatusUpdate(request, 'deactivationApproval');
    };

  const handleClickPendingInternational = () => {
    onStatusUpdate(request, 'international');
  };

  const handleClickPendingTestSecurity = () => {
    onStatusUpdate(request, 'testSecurity');
  };

  const handleClickOPSApproved = () => {
    onClickOpsApproved(request);
  };

  switch (request.processStatus) {
      case 'submitted':
      return (
        !isReviewer ?
            <ActionList>
              <ActionButton onClick={handleClickPendingReview}>
                <ArticleIcon />
                Pending Review
              </ActionButton>
            </ActionList> :
            <></>
      );
      case 'processing':
      return (
        !isReviewer ?
            (request.requestType === 'create' ?
                <ActionList>
                    <ActionButton onClick={handleClickOPSApproved}>
                    <CheckIcon />
                    Vetting Approved
                    </ActionButton>
                    <ActionButton onClick={handleClickDenied}>
                      <CloseIcon />
                      Denied
                    </ActionButton>
                    <ActionButton onClick={handleClickNeedMoreInformation}>
                      <CheckIcon />
                      Need More Information
                    </ActionButton>
                    { type === 'ai'&& 
                    <div>
                      <ActionButton onClick={handleClickPendingAppeal}>
                        <CheckIcon />
                        Pending Appeal
                      </ActionButton>
                      <br/>
                      <ActionButton onClick={handleClickPendingInternational}>
                        <CheckIcon />
                        Pending International
                      </ActionButton>
                      <br/>
                      <ActionButton onClick={handleClickPendingTestSecurity}>
                        <CheckIcon />
                        Pending Test Security
                      </ActionButton>
                    </div>
                  }
                </ActionList> :
                <ActionList>
                    <ActionButton onClick={handleClickOPSApproved}>
                      <CheckIcon />
                      Vetting Approved
                    </ActionButton>
                    <ActionButton onClick={handleClickDenied}>
                      <CloseIcon />
                      Denied
                    </ActionButton>
                    <ActionButton onClick={handleClickPendingDeactivationApproval}>
                      <CheckIcon />
                      Pending Deactivation Approval
                    </ActionButton>
                    { type === 'ai'&& 
                      <div>
                        <ActionButton onClick={handleClickPendingAppeal}>
                          <CheckIcon />
                          Pending Appeal
                        </ActionButton>
                        <br/>
                        <ActionButton onClick={handleClickPendingInternational}>
                          <CheckIcon />
                          Pending International
                        </ActionButton>
                      </div>
                    }
                </ActionList>) :
                <></>
      );
    case 'qcReadyReopen':
      return (
        !isReviewer ?
        (isQc ?
            (<ActionList>
                <ActionButton onClick={handleClickNeedMoreInformation}>
                  <CheckIcon />
                  QC OK
                </ActionButton>
                <ActionButton onClick={handleClickPendingReview}>
                    <CloseIcon />
                    QC Denied (Under Review)
                </ActionButton>
              </ActionList> ) :<></>)
              :
            <></>
    );
    case 'qcReadyRejected':
      return (
        !isReviewer ?
        (isQc ?
            (<ActionList>
                <ActionButton onClick={handleClickDenied}>
                    <CloseIcon />
                    QC OK
                </ActionButton>
                <ActionButton onClick={handleClickPendingReview}>
                  <EditIcon />
                  QC Denied(Under Review)
                </ActionButton>
              </ActionList> ) :<></>)
              :
            <></>
    );
    case 'qcReadyVetting':
      return (
        !isReviewer ?
        (isQc ?
            (<ActionList>
                <ActionButton onClick={handleClickOPSApproved}>
                    <CheckIcon />
                    QC Approved
                </ActionButton>
                <ActionButton onClick={handleClickPendingReview}>
                    <CloseIcon />
                    QC Denied (Under Review)
                </ActionButton>
              </ActionList> ) :<></>)
              :
            <></>
    );
    case 'appeal':
      return (
        !isReviewer ?
            (request.requestType === 'create' ?
              <ActionList>
                <ActionButton onClick={handleClickOPSApproved}>
                  <CheckIcon />
                  Vetting Approved
                </ActionButton>
                <ActionButton onClick={handleClickDenied}>
                  <CloseIcon />
                  Denied
                </ActionButton>
                <ActionButton onClick={handleClickNeedMoreInformation}>
                  <EditIcon />
                  Need More Information
                </ActionButton>
                { type === 'ai'&& 
                  <div>
                    <ActionButton onClick={handleClickPendingInternational}>
                      <EditIcon />
                      Pending International
                    </ActionButton>
                    <br/>
                    <ActionButton onClick={handleClickPendingTestSecurity}>
                      <EditIcon />
                      Pending Test Security
                    </ActionButton>
                  </div>
                }
              </ActionList> :
              <ActionList>
                <ActionButton onClick={handleClickOPSApproved}>
                  <CheckIcon />
                  Vetting Approved
                </ActionButton>
                <ActionButton onClick={handleClickDenied}>
                  <CloseIcon />
                  Denied
                </ActionButton>
              </ActionList> ) :
            <></>
      );
      case 'deactivationApproval':
          return (
              !isReviewer ?
                  (<ActionList>
                      <ActionButton onClick={handleClickOPSApproved}>
                          <CheckIcon />
                          Vetting Approved
                      </ActionButton>
                      <ActionButton onClick={handleClickDenied}>
                          <CloseIcon />
                          Denied
                      </ActionButton>
                    </ActionList> ) :
                  <></>
          );
    case 'international':
      return (
       !isReviewer ?
           (<ActionList>
              <ActionButton onClick={handleClickOPSApproved}>
                <CheckIcon />
                Vetting Approved
              </ActionButton>
              <ActionButton onClick={handleClickDenied}>
                <CloseIcon />
                Denied
              </ActionButton>
              { type === 'ai' && request.requestType === 'create' &&
                <ActionButton onClick={handleClickNeedMoreInformation}>
                  <EditIcon />
                  Need More Information
                 </ActionButton>
              }
              { type === 'ai'&& 
               <div>
                <ActionButton onClick={handleClickPendingAppeal}>
                  <EditIcon />
                  Pending Appeal
                </ActionButton>
                <br/>
                <ActionButton onClick={handleClickPendingTestSecurity}>
                  <EditIcon />
                  Pending Test Security
                </ActionButton>
              </div>
              }
            </ActionList> ):
           <></>
      );
    case 'testSecurity':
      return (
        !isReviewer ?
            (<ActionList>
              <ActionButton onClick={handleClickOPSApproved}>
                <CheckIcon />
                Vetting Approved
              </ActionButton>
              <ActionButton onClick={handleClickDenied}>
                <CloseIcon />
                Denied
              </ActionButton>
              { type === 'ai' && request.requestType === 'create' &&
              <ActionButton onClick={handleClickNeedMoreInformation}>
                <EditIcon />
                Need More Information
              </ActionButton>
              }
              { type === 'ai'&& 
               <div>
                  <ActionButton onClick={handleClickPendingAppeal}>
                    <EditIcon />
                    Pending Appeal
                  </ActionButton>
                  <br/>
                  <ActionButton onClick={handleClickPendingInternational}>
                    <EditIcon />
                    Pending International
                  </ActionButton>
                </div>
              }
            </ActionList> ) :
            <></>
      );
    case 'opsApproved':
      return (
        !isReviewer ?
            (isAdmin ?
              <ActionList>
                <ActionButton onClick={handleClickApproved}>
                  <CheckIcon />
                  Approve
                </ActionButton>
                <ActionButton onClick={handleXpmDenied}>
                  <CloseIcon />
                  XPM Denied
                </ActionButton>
                { type === 'ai'&& request.requestType !== 'create' &&
                    <div>
                      <ActionButton onClick={handleClickPendingInternational}>
                        <CheckIcon />
                        Pending International
                      </ActionButton>
                    </div>
                }
              </ActionList> 
              :
              <ActionList>
                <ActionButton onClick={handleClickApproved}>
                  <CheckIcon />
                  View Changes
                </ActionButton>
                { request?.ingestionPathCd?.toString() !== "2" &&
                    <div>
                      <ActionButton onClick={handleClickPendingReview}>
                      <EditIcon />
                       Under Review
                      </ActionButton>
                    </div>
                }
              </ActionList> ) :
            <></>
      );
    case 'xpmDenied':
      return (
        !isReviewer ?
            (<ActionList>
              <ActionButton onClick={handleClickOPSApproved}>
                <CheckIcon />
                Vetting Approved
              </ActionButton>
              <ActionButton onClick={handleClickDenied}>
                <CloseIcon />
                Denied
              </ActionButton>
              {request.requestType === 'create' && 
                <div>
                  <ActionButton onClick={handleClickNeedMoreInformation}>
                    <EditIcon />
                    Need More Information
                  </ActionButton>
                </div>
              }
              { type === 'ai'&& 
                <div>
                  <ActionButton onClick={handleClickPendingAppeal}>
                    <EditIcon />
                    Pending Appeal
                  </ActionButton> 
                  <br/>
                  <ActionButton onClick={handleClickPendingInternational}>
                    <EditIcon />
                    Pending International
                  </ActionButton>
                  <br/>
                  <ActionButton onClick={handleClickPendingTestSecurity}>
                    <EditIcon />
                    Pending Test Security
                  </ActionButton>
                </div>
              }
            </ActionList> ) :
            <></>
      );

    default:
      return null;
  }
};
